<script lang="ts" setup>
import type { VBtn } from 'vuetify/components'

type VuetifyVariant = Exclude<VBtn['$props']['variant'], undefined>

type ButtonType = 'primary' | 'secondary' | 'text'

export type ButtonColor =
  | 'primary'
  | 'primary-xqes'
  | 'secondary-container-xqes'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'ses'
  | 'aes'
  | 'qes'
  | 'part11'
  | 'demo'
  | 'grey'
  | 'grey-dark'
  | 'grey-darker'
  | 'grey-fine'
  | 'grey-light-5'
  | 'white'
  | 'purple-dark'
  | 'grey-bg-2'
  | 'black'
  | 'blue-light-4'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface ButtonProps {
  type?: ButtonType
  color?: ButtonColor
  size?: ButtonSize
  icon?: string | boolean
  block?: boolean
  disabled?: boolean
  loading?: boolean
  rounded?: string | boolean
  ripple?: boolean
  href?: string
  to?: string | Record<string, unknown>
  newTab?: boolean
}

const props = withDefaults(defineProps<ButtonProps>(), {
  type: 'primary',
  size: 'md',
  rounded: 'default',
  newTab: true,
})

const emit = defineEmits<{
  click: []
}>()

const lookup: { sizes: Record<ButtonSize, string>; variants: Partial<Record<ButtonType, VuetifyVariant>> } = {
  sizes: {
    xs: 'x-small',
    sm: 'small',
    md: 'default',
    lg: 'large',
    xl: 'x-large',
  },
  variants: {
    secondary: 'outlined',
    text: 'text',
  },
}

const inheritedSize = inject<ButtonSize | null>('buttonSize', null)

const computedSize = computed(() => {
  if (inheritedSize) return lookup.sizes[inheritedSize]
  return lookup.sizes[props.size]
})

const computedVariant = computed<VuetifyVariant>(() => {
  return lookup.variants[props.type] ?? 'elevated'
})

const computedColor = computed<ButtonColor>(() => {
  if (props.color) return props.color
  if (props.type === 'primary') {
    return 'info'
  } else if (props.type === 'secondary') {
    return 'info'
  } else {
    return 'grey'
  }
})

const textColor = computed(() => {
  if (props.color && ['ses', 'aes', 'qes', 'part11'].includes(props.color)) {
    return 'white'
  }
  if (props.color && ['grey-light-5'].includes(props.color)) {
    return 'skribbleu'
  }
  if (props.color === 'blue-light-4') {
    return 'primary'
  }
  return undefined
})

const computedClass = computed(() => {
  return {
    'text-white': textColor.value === 'white',
    'text-skribbleu': textColor.value === 'skribbleu',
    'text-primary': textColor.value === 'primary',
  }
})

const onClick = () => {
  if (!props.disabled && !props.loading) {
    emit('click')

    if (props.href && props.newTab) {
      window.open(props.href, '_blank', 'noopener')
    } else if (props.to) {
      navigateTo(props.to)
    }
  }
}

const buttonRef = useTemplateRef('buttonRef')

defineExpose({
  blur: () => {
    buttonRef.value?.$el.blur()
  },
})
</script>

<template>
  <v-btn
    ref="buttonRef"
    class="skr-button"
    :class="computedClass"
    :color="computedColor"
    :variant="computedVariant"
    :ripple="ripple"
    :icon="icon"
    :href="href"
    :size="computedSize"
    :rounded="rounded"
    :disabled="disabled"
    :loading="loading"
    :block="block"
    @click="onClick"
  >
    <template v-if="!icon" #default>
      <slot />
    </template>
  </v-btn>
</template>
